import Immutable from 'immutable';
import { type MediaSourceData, VideoData } from '@lumen5/luminary';

import type {
    AIAvatar,
    AIAvatarVoicePreset,
    BatchTextToVoiceoverResponse,
    SlideBackgroundModel,
} from 'js/src/client';
import { SlideBackgroundModelTypeEnum, SourceEnum } from 'js/src/client';
import type { ScriptSentenceData } from 'js/src/data/ai-voices-and-avatars/VoiceoverCache';
import { VoiceoverCache } from 'js/src/data/ai-voices-and-avatars/VoiceoverCache';
import { BatchAnalytics } from 'js/src/libs/BatchAnalytics';
import type { ImmutableMap } from 'js/src/libs/consts';
import { UNKNOWN_LANGUAGE_CODE, WORD_DURATION_ESTIMATE_SECONDS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA } from 'js/src/libs/scene-utils';
import uuid from 'js/src/libs/uuid';

/**
 * Reads audio duration from a path/url and caches it unto the given key in session storage
 */
export function cacheVoiceoverDuration(key: string, path: string) {
    const audioElement = new Audio(path);
    audioElement.addEventListener('loadeddata', () => {
        try {
            sessionStorage.setItem(key, audioElement.duration.toFixed(2));
        } catch (error) {
            // The cache is probably full, we should clear it and try again.
            sessionStorage.clear();
            sessionStorage.setItem(key, audioElement.duration.toFixed(2));
        }
    });
}

/**
 * Generate AI voiceover for a given text + voice pair
 * Responses are cached in the session storage to avoid re-fetching the same text + voice pair
 */
export function convertTextToVoiceover(
    text: string,
    voice_preset: string,
    regenerate = false,
    language_code?: string,
) {
    return new Promise<string>((resolve, reject) => {
        let shouldFetch = true;
        const useTransientBucket = true;
        const cache = new VoiceoverCache();
        if (regenerate) {
            cache.delete(text, voice_preset, undefined, useTransientBucket);
        }
        // We don't need to re-fetch & re-generate the voiceover if we already have for the text + voice pair
        // Therefore we cache the response in the session storage so that subsequent calls use the cache
        const response = cache.get(text, voice_preset, undefined, useTransientBucket);
        const { url } = response ?? {};
        if (url) {
            shouldFetch = false;
            resolve(url);
        }
        if (shouldFetch) {
            batchConvertTextToVoiceover(
                [text],
                voice_preset,
                regenerate,
                language_code,
                undefined,
                useTransientBucket,
            )
                .then(({ urls }) => {
                    resolve(urls[0]);
                })
                .catch((error) => {
                    reject(error);
                });
        }
    });
}

/**
 * Store voiceover urls in the cache and track character usage
 */
export function processUrls(useTransientBucket = false) {
    return (response: BatchTextToVoiceoverResponse): void => {
        const cache = new VoiceoverCache();
        response.blocks.forEach((block) => {
            const voiceSpeed = block.voice_speed ?? 1;
            if (!cache.get(block.text, block.voice_preset, voiceSpeed, useTransientBucket)) {
                if (
                    !block.url ||
                    block.script_sentence === null ||
                    block.audible_duration === null
                ) {
                    ErrorReporter.addBreadcrumb({
                        message: 'Block failed to generate voiceover',
                        data: { block },
                    });
                    return;
                }
                cache.set(
                    block.text,
                    block.voice_preset,
                    {
                        url: block.url,
                        sentence_data: {
                            script_sentence: block.script_sentence,
                            audible_duration: block.audible_duration,
                        },
                    },
                    undefined,
                    voiceSpeed,
                    useTransientBucket,
                );
                BatchAnalytics.track(
                    'Elevenlabs Character Usage',
                    {},
                    {
                        num_characters: block.text.length,
                    },
                );
            }
        });
    };
}

function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

export function avatarVideoAsset(avatar: ImmutableMap<AIAvatar>) {
    const previewUrl = avatar.get('preview_url');
    if (!previewUrl) {
        throw new Error(`Avatar ${avatar.get('name')} does not have a preview url`);
    }
    return Immutable.fromJS({
        type: SlideBackgroundModelTypeEnum.VIDEO,
        video: {
            id: uuid(),
            uuid: uuid(),
            source: SourceEnum.SOURCE_AI_AVATAR,
            source_id: uuid(),
            license: VideoData.USER_PRIVATE_LICENSE,
            attribution: null,
            is_editorial: null,
            width: 1080,
            height: 1080,
            thumbnail_url: previewUrl,
            preview_url: previewUrl,
            render_url: null,
            poster_url: avatar.get('thumbnail_url'),
            duration: 10000,
            has_audio: false,
            has_voiceover: true,
            ai_avatar: avatar.get('id'),
            description: null,
            tags: [],
            title: null,
            display_credit_text: null,
            display_credit_url: null,
            captions: null,
        },
        image: null,
        icon: null,
        history_index_user_ids: [],
    }) as unknown as ImmutableMap<SlideBackgroundModel>;
}

export function avatarBackgroundVideoAsset(
    preset: ImmutableMap<AIAvatarVoicePreset> | null,
): MediaSourceData {
    const backgroundPreviewUrl = preset && preset.get('background_preview_url');
    const backgroundRenderUrl = preset && preset.get('background_render_url');

    // Start with placeholder and only override URLs when available
    return {
        ...AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA,
        ...(backgroundPreviewUrl && { preview_url: backgroundPreviewUrl }),
        ...(backgroundRenderUrl && { render_url: backgroundRenderUrl }),
        ...(backgroundPreviewUrl && { thumbnail_url: backgroundPreviewUrl }),
    };
}

export async function batchConvertTextToVoiceover(
    scriptSentences: Array<string>,
    voicePreset: string,
    regenerate = false,
    language_code: string = UNKNOWN_LANGUAGE_CODE,
    voiceSpeed: number = 1,
    useTransientBucket = false,
): Promise<{ urls: string[]; sentence_data: ScriptSentenceData[] }> {
    const cache = new VoiceoverCache();
    await cache.fetchSentences({
        scriptSentences,
        voicePreset,
        language_code,
        regenerate,
        voiceSpeed,
        useTransientBucket,
    });

    // Get all the voiceover urls from the cache
    const urls = scriptSentences
        .map((sentence) => cache.get(sentence, voicePreset, voiceSpeed, useTransientBucket)?.url)
        .filter(isNotNullOrUndefined);
    const sentence_data = scriptSentences
        .map(
            (sentence) =>
                cache.get(sentence, voicePreset, voiceSpeed, useTransientBucket)?.sentence_data,
        )
        .filter(isNotNullOrUndefined);
    return { urls, sentence_data };
}

export function getEstimatedVoiceoverDuration(text: string): number {
    // TODO: Use language-aware word splitting https://lumen5.atlassian.net/browse/MR-3356
    const wordCount = text.split(' ').length;
    return wordCount * WORD_DURATION_ESTIMATE_SECONDS;
}

export function getOrEstimateVoiceoverDuration(text: string): number {
    text = text.toLowerCase().trim();
    const duration = parseFloat(sessionStorage.getItem(text) ?? 'NaN');
    if (isNaN(duration)) {
        return getEstimatedVoiceoverDuration(text);
    }
    return duration;
}
